/**
 * Skeletal landmark anchors — clinically useful bony references rendered as
 * pins on the anatomy figure. Coordinates are in viewBox 0..100 space.
 *
 * These are not muscle attachment points; they are the references a therapist
 * uses for orientation ("locate the C7 spinous process, count down two for T2…").
 *
 * Each entry: { id, label, view, x, y, palpation, clinical }
 *  - palpation: how the therapist physically finds the landmark
 *  - clinical:  why it matters in practice
 */
export const SKELETAL_LANDMARKS = [
  // ── Posterior view ────────────────────────────────────────────
  {
    id: "occipital_protuberance",
    label: "External occipital protuberance",
    view: "posterior",
    x: 50, y: 14,
    palpation: "Midline bump at the base of the skull, posterior.",
    clinical: "Reference for ligamentum nuchae and trapezius origin; orient cervical work.",
  },
  {
    id: "c7_spinous",
    label: "C7 spinous process",
    view: "posterior",
    x: 50, y: 22,
    palpation: "Most prominent spinous process at the base of the neck; flex the head to confirm.",
    clinical: "Top reference for counting thoracic levels. Vertebra prominens.",
  },
  {
    id: "t3_spine_of_scapula",
    label: "T3 / spine of scapula (medial root)",
    view: "posterior",
    x: 50, y: 28,
    palpation: "Trace the medial scapular border to its highest point — that meets T3.",
    clinical: "Landmark for scapular elevation and upper-thoracic mobility assessment.",
  },
  {
    id: "t7_inferior_scapular_angle",
    label: "T7 / inferior angle of scapula",
    view: "posterior",
    x: 50, y: 36,
    palpation: "Bottom tip of the scapula sits at the T7 vertebral level.",
    clinical: "Mid-thoracic anchor; useful for rib evaluation.",
  },
  {
    id: "l4_iliac_crest",
    label: "L4 / iliac crest line",
    view: "posterior",
    x: 50, y: 49,
    palpation: "Tuffier's line — top of the iliac crests, level with L4 spinous.",
    clinical: "Reference for lumbar segment counting and SI joint orientation.",
  },
  {
    id: "psis",
    label: "PSIS (posterior superior iliac spine)",
    view: "posterior",
    x: 45, y: 53,
    palpation: "Dimple at the upper sacrum, just lateral to S2.",
    clinical: "Pelvic alignment + SI mobility check; reference for gluteus medius origin.",
  },
  {
    id: "psis_right",
    label: "PSIS (right)",
    view: "posterior",
    x: 55, y: 53,
    palpation: "Dimple at the upper sacrum, just lateral to S2.",
    clinical: "Pelvic alignment + SI mobility check; reference for gluteus medius origin.",
  },
  {
    id: "greater_trochanter",
    label: "Greater trochanter",
    view: "posterior",
    x: 38, y: 58,
    palpation: "Bony prominence on the lateral hip ~1 hand-width below the iliac crest.",
    clinical: "Hip rotation pivot; bursitis reference; piriformis insertion landmark.",
  },
  {
    id: "greater_trochanter_right",
    label: "Greater trochanter (right)",
    view: "posterior",
    x: 62, y: 58,
    palpation: "Bony prominence on the lateral hip ~1 hand-width below the iliac crest.",
    clinical: "Hip rotation pivot; bursitis reference; piriformis insertion landmark.",
  },

  // ── Anterior view ─────────────────────────────────────────────
  {
    id: "mastoid_process",
    label: "Mastoid process",
    view: "anterior",
    x: 55, y: 13,
    palpation: "Bony point behind the earlobe.",
    clinical: "SCM and splenius capitis insertion. Reference for upper cervical orientation.",
  },
  {
    id: "mastoid_process_right",
    label: "Mastoid process (right)",
    view: "anterior",
    x: 45, y: 13,
    palpation: "Bony point behind the earlobe.",
    clinical: "SCM and splenius capitis insertion.",
  },
  {
    id: "sternal_notch",
    label: "Jugular (sternal) notch",
    view: "anterior",
    x: 50, y: 22,
    palpation: "U-shaped depression at the top of the sternum.",
    clinical: "Midline reference; level approximates T2/T3.",
  },
  {
    id: "sternoclavicular_joint",
    label: "Sternoclavicular joint",
    view: "anterior",
    x: 46, y: 23,
    palpation: "Lateral to sternal notch, where the clavicle meets the sternum.",
    clinical: "First-rib elevation and SCM tension assessment.",
  },
  {
    id: "acromion",
    label: "Acromion",
    view: "anterior",
    x: 68, y: 26,
    palpation: "Flat bony shelf at the top of the shoulder.",
    clinical: "Shoulder height comparison; AC joint pathology; deltoid attachment.",
  },
  {
    id: "acromion_right",
    label: "Acromion (right)",
    view: "anterior",
    x: 32, y: 26,
    palpation: "Flat bony shelf at the top of the shoulder.",
    clinical: "Shoulder height comparison; AC joint pathology.",
  },
  {
    id: "xiphoid",
    label: "Xiphoid process",
    view: "anterior",
    x: 50, y: 36,
    palpation: "Small bony tip at the bottom of the sternum.",
    clinical: "Diaphragm attachment; midline reference for thoracic level T9.",
  },
  {
    id: "asis",
    label: "ASIS (anterior superior iliac spine)",
    view: "anterior",
    x: 57, y: 52,
    palpation: "Pointy front edge of the iliac crest.",
    clinical: "Pelvic tilt check (ASIS vs PSIS); origin of sartorius and TFL.",
  },
  {
    id: "asis_right",
    label: "ASIS (right)",
    view: "anterior",
    x: 43, y: 52,
    palpation: "Pointy front edge of the iliac crest.",
    clinical: "Pelvic tilt check; sartorius / TFL origin.",
  },
  {
    id: "pubic_symphysis",
    label: "Pubic symphysis",
    view: "anterior",
    x: 50, y: 58,
    palpation: "Bony midline at the top of the pubic bone.",
    clinical: "Adductor origin; pelvic floor reference; SI alignment check.",
  },
  {
    id: "patella",
    label: "Patella",
    view: "anterior",
    x: 57, y: 73,
    palpation: "Kneecap.",
    clinical: "Quad tendon insertion; patellar tracking assessment.",
  },
  {
    id: "patella_right",
    label: "Patella (right)",
    view: "anterior",
    x: 43, y: 73,
    palpation: "Kneecap.",
    clinical: "Quad tendon insertion; patellar tracking assessment.",
  },
  {
    id: "tibial_tuberosity",
    label: "Tibial tuberosity",
    view: "anterior",
    x: 57, y: 77,
    palpation: "Bony bump on the tibia just below the patella.",
    clinical: "Patellar tendon insertion; Osgood-Schlatter reference.",
  },
  {
    id: "tibial_tuberosity_right",
    label: "Tibial tuberosity (right)",
    view: "anterior",
    x: 43, y: 77,
    palpation: "Bony bump on the tibia just below the patella.",
    clinical: "Patellar tendon insertion.",
  },
];

export const LANDMARKS_BY_VIEW = SKELETAL_LANDMARKS.reduce((acc, l) => {
  (acc[l.view] = acc[l.view] || []).push(l);
  return acc;
}, {});
